import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Account, type NotificationSettings } from '../api';
import { useMe } from '../queries';
import { useSignOut } from './Shell';
import { AccountBadges } from '../ui';

export function Settings() {
  const me = useMe();
  const signOut = useSignOut();
  if (!me.data) return null;
  const { account, user } = me.data;
  return (
    <section className="page" style={{ maxWidth: 820 }}>
      <h1>Settings</h1>
      <section className="card">
        <h2>Account</h2>
        <div className="kv">
          <div>
            <span className="k">Name</span>
            <span>
              {account.name}
              <span className="badges">
                <AccountBadges tier={account.trust_tier} billing={account.billing_mode ?? 'prepaid'} />
              </span>
            </span>
          </div>
          <div>
            <span className="k">Account email</span>
            <span>{account.email}</span>
          </div>
          <div>
            <span className="k">Level</span>
            <span>
              {account.trust_tier === 'partner' ? 'Partner: up to 1080p, 50 live streams, 24 h each' : account.trust_tier === 'trusted' ? 'Trusted: up to 1080p, 10 live streams, 24 h each' : 'New: up to 720p, 1 live stream, 4 h each'}{' '}
              <a className="faint" href="https://docs.justonair.com/limits#account-limits" target="_blank" rel="noreferrer">
                Limits
              </a>
            </span>
          </div>
          <div>
            <span className="k">Billing</span>
            <span>
              {account.billing_mode === 'postpaid'
                ? 'Postpaid: charged daily, the balance can go below zero'
                : account.billing_mode === 'internal'
                  ? 'Internal: charged daily for the numbers, never stopped'
                  : 'Prepaid: credit first, streams stop at zero'}{' '}
              {account.billing_mode !== 'internal' && (
                <a
                  className="faint"
                  href={`https://docs.justonair.com/pricing#${account.billing_mode === 'postpaid' ? 'postpaid-accounts' : 'how-billing-works'}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  How billing works
                </a>
              )}
            </span>
          </div>
          <div>
            <span className="k">Recordings are kept</span>
            <span>{account.recording_retention_days == null ? 'Until deleted' : `${account.recording_retention_days} days by default`}</span>
          </div>
          <div>
            <span className="k">Account id</span>
            <span className="mono" style={{ fontSize: 12 }}>{account.id}</span>
          </div>
        </div>
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>Need higher limits or a different retention? Email ops@justonair.com.</p>
      </section>
      {account.billing_mode !== 'internal' && <EmailSettings account={account} />}
      <section className="card">
        <h2>You</h2>
        <div className="kv">
          <div>
            <span className="k">Signed in as</span>
            <span>{user.email}</span>
          </div>
        </div>
        <div>
          <button type="button" className="btn" onClick={signOut}>
            Sign out
          </button>
        </div>
      </section>
    </section>
  );
}

/** Warnings before credit runs out. Saved with PATCH /v1/account/notifications. */
function EmailSettings({ account }: { account: Account }) {
  const qc = useQueryClient();
  const saved = account.notifications;
  const [low, setLow] = useState(saved.low_balance_email);
  const [amount, setAmount] = useState(saved.low_balance_threshold_usd);
  const [rec, setRec] = useState(saved.recording_deletion_email);
  const [stmt, setStmt] = useState(saved.balance_statement_email ?? true);
  const postpaid = account.billing_mode === 'postpaid';
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Another tab or an API call changed them: show what is stored.
  useEffect(() => {
    setLow(saved.low_balance_email);
    setAmount(saved.low_balance_threshold_usd);
    setRec(saved.recording_deletion_email);
    setStmt(saved.balance_statement_email ?? true);
  }, [saved.low_balance_email, saved.low_balance_threshold_usd, saved.recording_deletion_email, saved.balance_statement_email]);

  const amountNum = Number(amount);
  const amountOk = amount.trim() !== '' && Number.isFinite(amountNum) && amountNum >= 1 && amountNum <= 10000 && /^\d+(\.\d{1,2})?$/.test(amount.trim());
  const dirty =
    low !== saved.low_balance_email ||
    rec !== saved.recording_deletion_email ||
    stmt !== (saved.balance_statement_email ?? true) ||
    (low && amountOk && amountNum.toFixed(2) !== saved.low_balance_threshold_usd);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (low && !amountOk) {
      setMsg({ ok: false, text: 'Enter an amount between 1 and 10,000 USD, with at most two decimals.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const body: Partial<Record<keyof NotificationSettings, unknown>> = { low_balance_email: low, recording_deletion_email: rec };
      if (postpaid) body.balance_statement_email = stmt;
      if (low) body.low_balance_threshold_usd = amount.trim();
      const next = await api<NotificationSettings>('/v1/account/notifications', { method: 'PATCH', json: body });
      setAmount(next.low_balance_threshold_usd);
      await qc.invalidateQueries({ queryKey: ['me'] });
      setMsg({ ok: true, text: 'Saved.' });
    } catch (err) {
      setMsg({ ok: false, text: err instanceof ApiError ? err.message : 'Could not save.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} aria-label="Emails">
      <h2>Emails</h2>
      <p className="faint" style={{ margin: 0, fontSize: 13 }}>
        Sent to {account.email}.{' '}
        {postpaid
          ? 'Your account can go below zero: nothing stops, and we tell you what is owed.'
          : 'When credit reaches zero, live streams stop and new ones are refused; after 7 days at zero, recordings are deleted.'}
      </p>

      <label className="check">
        <input type="checkbox" checked={low} onChange={(e) => setLow(e.target.checked)} />
        Low credit: when it drops below an amount, and when it runs out
      </label>
      {low && (
        <label className="field" style={{ maxWidth: 220, marginLeft: 26 }}>
          Warn me below (USD)
          <input
            className="input"
            type="number"
            inputMode="decimal"
            min={1}
            max={10000}
            step={0.01}
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
      )}

      {postpaid ? (
        <label className="check">
          <input type="checkbox" checked={stmt} onChange={(e) => setStmt(e.target.checked)} />
          Balance owed: when it goes below zero, every 14 days while it stays there, and at 100 / 500 / 1,000 USD
        </label>
      ) : (
        <label className="check">
          <input type="checkbox" checked={rec} onChange={(e) => setRec(e.target.checked)} />
          Recordings about to be deleted: when the 7 days start, and 24 hours before
        </label>
      )}

      <p className="hint" style={{ margin: 0 }}>
        Webhooks are separate: each endpoint gets the events it subscribes to (credit.low, credit.depleted, recordings.deletion_scheduled). The
        amount above also sets when credit.low fires.
      </p>

      <div className="row">
        <button type="submit" className="btn primary" disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {msg && (
          <span className={msg.ok ? 'muted' : 'error-text'} role={msg.ok ? 'status' : 'alert'} style={{ fontSize: 13 }}>
            {msg.text}
          </span>
        )}
      </div>
    </form>
  );
}
